import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Moon, Sun, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useSpaData } from "../context/SpaDataContext";
import { useBooking } from "../context/BookingContext";
import { imgUrl } from "../lib/spa";

const LANGS = ["pt", "en", "es"];

const LangSwitcher = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {LANGS.map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="mx-1 text-foreground/20">·</span>}
          <button
            data-testid={`lang-${l}`}
            onClick={() => setLang(l)}
            className={`text-xs uppercase tracking-widest transition-colors duration-300 ${
              lang === l ? "text-gold" : "text-foreground/55 hover:text-foreground"
            }`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

const ThemeToggle = ({ testId = "theme-toggle" }) => {
  const { theme, toggle } = useTheme();
  return (
    <button
      data-testid={testId}
      onClick={toggle}
      aria-label="Alternar tema"
      className="text-foreground/65 transition-colors duration-300 hover:text-gold"
    >
      {theme === "dark" ? (
        <Sun className="h-[18px] w-[18px]" strokeWidth={1.5} />
      ) : (
        <Moon className="h-[18px] w-[18px]" strokeWidth={1.5} />
      )}
    </button>
  );
};

const Dropdown = ({ id, label, items, allLabel, allTo, active }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={`nav-dropdown-${id}`}
        className={`flex items-center gap-1.5 text-sm tracking-wide transition-colors duration-300 ${
          active ? "text-gold" : "text-foreground/82 hover:text-foreground"
        }`}
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      <div
        className={`absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-4 transition-all duration-300 ${
          open ? "visible opacity-100 translate-y-0" : "invisible opacity-0 -translate-y-2"
        }`}
      >
        <div className="overflow-hidden rounded-xl border border-border bg-popover/95 shadow-2xl shadow-black/40 backdrop-blur-xl">
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-item-${item.testId}`}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground/82 transition-colors duration-200 hover:bg-foreground/5 hover:text-gold"
              >
                {item.img && (
                  <img src={item.img} alt="" className="h-8 w-8 rounded-full object-cover" loading="lazy" />
                )}
                {item.label}
              </Link>
            ))}
          </div>
          <Link
            to={allTo}
            data-testid={`nav-all-${id}`}
            className="block border-t border-foreground/10 px-4 py-3 text-center text-xs uppercase tracking-[0.2em] text-gold transition-colors duration-200 hover:bg-gold/10"
          >
            {allLabel}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, ml } = useLanguage();
  const { therapists, massages } = useSpaData();
  const { openBooking } = useBooking();
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const therapistItems = therapists.map((th) => ({
    to: `/terapeutas/${th.slug}`,
    label: th.name,
    img: imgUrl(th.profile),
    testId: `therapist-${th.slug}`,
  }));
  const massageItems = massages.map((m) => ({
    to: `/massagens/${m.slug}`,
    label: ml(m.name),
    testId: `massage-${m.slug}`,
  }));

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 ${
      isActive ? "text-gold" : "text-foreground/82 hover:text-foreground"
    }`;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 bg-background/80 backdrop-blur-xl transition-all duration-500 ${
          scrolled ? "border-b border-border" : ""
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo compact={scrolled} />

          <nav className="hidden items-center gap-8 lg:flex">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">
              {t("nav.home")}
            </NavLink>
            <Dropdown
              id="therapists"
              label={t("nav.therapists")}
              items={therapistItems}
              allLabel={t("nav.allTherapists")}
              allTo="/terapeutas"
              active={location.pathname.startsWith("/terapeutas")}
            />
            <Dropdown
              id="massages"
              label={t("nav.massages")}
              items={massageItems}
              allLabel={t("nav.allMassages")}
              allTo="/massagens"
              active={location.pathname.startsWith("/massagens")}
            />
            <NavLink to="/sobre" className={linkCls} data-testid="nav-about">
              {t("nav.about")}
            </NavLink>
            <NavLink to="/promocoes" className={linkCls} data-testid="nav-promotions">
              {t("nav.promotions")}
            </NavLink>
            <NavLink to="/candidaturas" className={linkCls} data-testid="nav-applications">
              {t("nav.applications")}
            </NavLink>
            <NavLink to="/contactos" className={linkCls} data-testid="nav-contact">
              {t("nav.contact")}
            </NavLink>
          </nav>

          <div className="hidden items-center gap-6 lg:flex">
            <ThemeToggle />
            <LangSwitcher />
            <button
              data-testid="nav-book-button"
              onClick={() => openBooking()}
              className="rounded-full border border-gold/60 bg-gold/5 px-6 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-black"
            >
              {t("nav.book")}
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="text-foreground lg:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-30 bg-background/95 backdrop-blur-xl transition-opacity duration-400 lg:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="flex h-full flex-col overflow-y-auto px-6 pb-10 pt-28">
          <Link to="/" data-testid="mobile-nav-home" className="border-b border-foreground/10 py-4 font-display text-2xl text-foreground">
            {t("nav.home")}
          </Link>
          <MobileSection title={t("nav.therapists")} testId="mobile-therapists">
            {therapistItems.map((i) => (
              <Link key={i.to} to={i.to} className="flex items-center gap-3 py-2.5 text-foreground/82">
                <img src={i.img} alt="" className="h-8 w-8 rounded-full object-cover" />
                {i.label}
              </Link>
            ))}
            <Link to="/terapeutas" className="block py-2.5 text-sm uppercase tracking-widest text-gold">
              {t("nav.allTherapists")}
            </Link>
          </MobileSection>
          <MobileSection title={t("nav.massages")} testId="mobile-massages">
            {massageItems.map((i) => (
              <Link key={i.to} to={i.to} className="block py-2.5 text-foreground/82">
                {i.label}
              </Link>
            ))}
            <Link to="/massagens" className="block py-2.5 text-sm uppercase tracking-widest text-gold">
              {t("nav.allMassages")}
            </Link>
          </MobileSection>
          <Link to="/sobre" data-testid="mobile-nav-about" className="border-b border-foreground/10 py-4 font-display text-2xl text-foreground">
            {t("nav.about")}
          </Link>
          <Link to="/promocoes" data-testid="mobile-nav-promotions" className="border-b border-foreground/10 py-4 font-display text-2xl text-foreground">
            {t("nav.promotions")}
          </Link>
          <Link to="/candidaturas" data-testid="mobile-nav-applications" className="border-b border-foreground/10 py-4 font-display text-2xl text-foreground">
            {t("nav.applications")}
          </Link>
          <Link to="/contactos" data-testid="mobile-nav-contact" className="border-b border-foreground/10 py-4 font-display text-2xl text-foreground">
            {t("nav.contact")}
          </Link>
          <div className="mt-8 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <ThemeToggle testId="mobile-theme-toggle" />
              <LangSwitcher />
            </div>
            <button
              data-testid="mobile-book-button"
              onClick={() => {
                setMobileOpen(false);
                openBooking();
              }}
              className="rounded-full bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-black"
            >
              {t("nav.book")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const MobileSection = ({ title, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-foreground/10">
      <button
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between py-4 font-display text-2xl text-foreground"
      >
        {title}
        <ChevronDown className={`h-5 w-5 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      <div className={`overflow-hidden transition-all duration-400 ${open ? "max-h-[600px] pb-4" : "max-h-0"}`}>
        {children}
      </div>
    </div>
  );
};
